/**
 * Use Collaboration Hook
 *
 * Real-time collaborative editing bound directly to vfs-service's own Yjs
 * document for this file (see vfs-service's src/ws/yDocSocket.js — a
 * hand-rolled but wire-compatible y-websocket server, sharing the exact same
 * Y.Doc an agent's /patch call mutates, per ADR-001). Replaces the old
 * MongoDB-backed syncService: text content now merges via real CRDT (richer
 * than the old "whole-file replace on remote update" approach) and remote
 * cursor position is carried as custom awareness state, rendered through the
 * existing MonacoCollaborationManager.
 *
 * Who is who comes from the project's presence (utils/presenceClient.js): the
 * person's name, initials and colour slot are the same everywhere in the
 * project (header, tree, kanban, here), not a random colour per tab. The open
 * file and the cursor (with "typing" for 3 s after a keystroke) are published
 * there too; with "hide my cursor" (settings) neither the presence nor the
 * file's awareness gets the caret.
 *
 * The binding is made only once the doc has synced: before that the editor is
 * read only, so nothing typed can be lost when the server's text takes over
 * (MonacoBinding replaces the model's content with the doc's). Once bound, the
 * doc is the only writer of this file: `isLive()` tells the editor to keep its
 * own saves local (indexedDbFileStorage's `liveSynced`), which would otherwise
 * apply the same edit a second time through /patch.
 *
 * Losing the connection (offline, vfs-service restarting, the session expired): the editor reads only ('disconnected',
 * or 'unavailable' when it never synced; MonacoFileEditor says so) and this tab's doc is dropped. It reconnects with a
 * fresh doc that takes the server's text (when the browser is back online, and every RETRY_MS meanwhile), never
 * y-websocket's own reconnect with the old doc: the server may have let that file's doc go and read the file again
 * (newer, maybe written by an agent), and the old doc merged into it would bring the old text back, twice.
 */

import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { MonacoBinding } from 'y-monaco';
import config from '../config';
import { useAuth } from '../context/AuthContext';
import MonacoCollaborationManager from '../utils/monacoCollaborationManager';
import { registerAwareness, unregisterAwareness } from '../utils/collaborationAwarenessRegistry';
import useProjectPresence from './useProjectPresence';
import { getPresencePrivacy, setPresenceCursor, setPresenceView } from '../utils/presenceClient';
import { inkFor, tintFor } from '../utils/participantColors';

const TYPING_MS = 3000;

function vfsWsBase() {
  if (!config.vfsServiceUrl) {
    return null;
  }
  const base = config.vfsServiceUrl.startsWith('/') ? `${window.location.origin}${config.vfsServiceUrl}` : config.vfsServiceUrl;
  return base.replace(/\/+$/, '').replace(/^http/, 'ws');
}

/** The awareness `user` of this tab: the person as the project's presence knows them. */
function awarenessUser(you, user) {
  const id = you?.id || user?.sub || user?.email || 'anonymous';
  const name = you?.name || user?.name || user?.email || 'Anonymous';
  const slot = Number.isInteger(you?.slot) ? you.slot : 0;
  return { id, name, initials: you?.initials || name.slice(0, 2).toUpperCase(), slot, color: inkFor(slot), tint: tintFor(slot) };
}

const SYNC_TIMEOUT_MS = 8000;
// Reconnecting after the connection was lost: soon after a drop, then every RETRY_MS while it cannot sync.
const RECONNECT_SOON_MS = 2000;
const RETRY_MS = 15000;

const useCollaboration = (editor, monaco, file, projectId, serviceName) => {
  const { user } = useAuth();
  const managerRef = useRef(null);
  const liveRef = useRef(false);
  // 'off' (no file bound), 'connecting' (read only until the doc syncs), 'live', 'unavailable' (no sync in time:
  // the editor saves through REST as without live editing, and this file is not bound any more).
  const [status, setStatus] = useState('off');
  // A new connection (a fresh doc) after the last one was lost, and the file whose connection was lost (its editor
  // stays read only while it reconnects).
  const [attempt, setAttempt] = useState(0);
  const [lostPath, setLostPath] = useState(null);
  // This tab's cursor line and whether it is typing, for the editor's soft warnings.
  const [myCursor, setMyCursor] = useState(null);
  const presence = useProjectPresence(projectId, { name: user?.name || user?.email || '' });
  const identityRef = useRef(awarenessUser(presence.you, user));
  identityRef.current = awarenessUser(presence.you, user);
  const awarenessRef = useRef(null);
  const identityKey = JSON.stringify(identityRef.current);

  // Cursor decoration manager — one per mounted editor instance, independent
  // of which file/collab connection is currently active.
  useEffect(() => {
    if (!editor || !monaco || !file) {
      return undefined;
    }
    managerRef.current = new MonacoCollaborationManager(editor, monaco);
    return () => {
      managerRef.current?.dispose();
      managerRef.current = null;
    };
  }, [editor, monaco, file?.path || file?.name]);

  // The presence's name, initials and colour reach this file's awareness as soon as they are known.
  useEffect(() => {
    awarenessRef.current?.setLocalStateField('user', JSON.parse(identityKey));
  }, [identityKey]);

  // The actual Yjs connection: one Y.Doc + WebsocketProvider per open file,
  // torn down and recreated whenever the file/project/service changes.
  useEffect(() => {
    const wsBase = vfsWsBase();
    const filePath = file?.path || file?.name;
    if (!editor || !monaco || !filePath || !projectId || !serviceName || !wsBase) {
      return undefined;
    }

    const model = editor.getModel();
    if (!model) {
      return undefined;
    }

    // Same join convention as indexedDbFileStorage.js's vfsClient.writeFile
    // calls (`${serviceName}/${filePath}`) — must match exactly, since this
    // is the doc key vfs-service's docManager hydrates from disk.
    const relPath = `${serviceName}/${filePath}`;

    const ydoc = new Y.Doc();
    const ytext = ydoc.getText('content');
    const provider = new WebsocketProvider(
      `${wsBase}/ws/v1/projects/${encodeURIComponent(projectId)}`,
      'doc',
      ydoc,
      { params: { path: relPath } }
    );
    const { awareness } = provider;
    awarenessRef.current = awareness;
    awareness.setLocalStateField('user', identityRef.current);
    setPresenceView(projectId, 'editor', { kind: 'file', path: relPath, service: serviceName });

    // The editor is read only until the doc has synced (see the header comment); MonacoFileEditor reads `status`.
    setStatus('connecting');
    let gaveUp = false;
    let retryTimer = null;
    const reconnect = () => setAttempt((n) => n + 1);
    // Stops this connection (no reconnect with this doc) and tries again with a new one.
    const lose = (nextStatus, retryMs) => {
      gaveUp = true;
      liveRef.current = false;
      provider.disconnect();
      setStatus(nextStatus);
      setLostPath(relPath);
      clearTimeout(retryTimer);
      retryTimer = setTimeout(reconnect, retryMs);
    };
    const syncTimer = setTimeout(() => {
      if (binding) {
        return;
      }
      lose('unavailable', RETRY_MS);
    }, SYNC_TIMEOUT_MS);
    const onStatus = ({ status: socketStatus } = {}) => {
      if (socketStatus === 'disconnected' && binding && !gaveUp) {
        lose('disconnected', RECONNECT_SOON_MS);
      }
    };
    provider.on('status', onStatus);
    const onOnline = () => {
      if (gaveUp) {
        reconnect();
      }
    };
    window.addEventListener('online', onOnline);

    let binding = null;
    let undoManager = null;
    let typingUntil = 0;
    let typingTimer = null;
    let cursorThrottle = null;
    const publishCursor = () => {
      const position = editor.getPosition();
      if (!position) {
        return;
      }
      const typing = Date.now() < typingUntil;
      setMyCursor((current) => (current?.line === position.lineNumber && current?.typing === typing ? current : { line: position.lineNumber, typing }));
      if (getPresencePrivacy().hideCursor) {
        awareness.setLocalStateField('cursor', null);
        setPresenceCursor(projectId, null);
        return;
      }
      const sel = editor.getSelection();
      const selection = sel && !sel.isEmpty()
        ? { startLine: sel.startLineNumber, startColumn: sel.startColumn, endLine: sel.endLineNumber, endColumn: sel.endColumn }
        : null;
      awareness.setLocalStateField('cursor', { line: position.lineNumber, column: position.column, selection, typing });
      setPresenceCursor(projectId, { path: relPath, line: position.lineNumber, column: position.column, ...(selection ? { selection } : {}), typing });
    };
    const handleCursorChange = () => {
      if (cursorThrottle) {
        clearTimeout(cursorThrottle);
      }
      cursorThrottle = setTimeout(publishCursor, 150);
    };
    const cursorDisposable = editor.onDidChangeCursorPosition(handleCursorChange);
    const selectionDisposable = editor.onDidChangeCursorSelection(handleCursorChange);

    // "Typing" for TYPING_MS after this tab's own edit (the binding is the origin of local edits), then not.
    const onLocalEdit = (_event, transaction) => {
      if (!binding || transaction.origin !== binding) {
        return;
      }
      typingUntil = Date.now() + TYPING_MS;
      handleCursorChange();
      clearTimeout(typingTimer);
      typingTimer = setTimeout(publishCursor, TYPING_MS + 50);
    };
    ytext.observe(onLocalEdit);

    const bind = () => {
      if (binding || gaveUp) {
        return;
      }
      clearTimeout(syncTimer);
      binding = new MonacoBinding(ytext, model, new Set([editor]), awareness);

      // Task #215 — undo/redo wasn't "synced to other live viewers": y-monaco's
      // MonacoBinding (see node_modules/y-monaco/src/y-monaco.js) applies BOTH
      // local edits AND remote peers' incoming changes to the SAME Monaco model
      // via `monacoModel.applyEdits(...)` — and Monaco pushes every one of
      // those onto its one native undo stack for that model, with no origin
      // tagging. Concretely: if a remote peer's edit is the most recent entry
      // in *your* local undo stack, pressing Ctrl+Z reverts THEIR change, not
      // yours — and because that revert is itself just a content diff pushed
      // back through the same MonacoBinding, it silently propagates to
      // everyone as if you'd intentionally edited their text. There is no
      // actual bug in the network sync (every keystroke/undo genuinely is a
      // real Yjs transaction that reaches every other viewer) — the bug is
      // that Monaco's single shared undo stack doesn't distinguish "my edit"
      // from "an edit that just arrived over the wire", so what gets
      // undone/redone is unpredictable and looks broken from every other
      // viewer's perspective.
      //
      // Fix: a `Y.UndoManager` scoped to `trackedOrigins: {binding}` — the
      // exact transaction origin MonacoBinding's own `_monacoChangeHandler`
      // uses for LOCAL edits (`this.doc.transact(fn, this)`, `this` being the
      // binding instance) — only records this user's own edits, never a
      // remote peer's. `undoManager.undo()`/`.redo()` still produce a real
      // Yjs transaction (broadcast to every connected client exactly like a
      // normal edit), so the effect of an undo IS visible to other live
      // viewers — it just now reverts the right thing. Monaco's own
      // Ctrl+Z/Ctrl+Shift+Z/Ctrl+Y keybindings have to be rebound to call it
      // explicitly: registering a command on the exact same keychord is
      // Monaco's documented way to override a built-in one.
      undoManager = new Y.UndoManager(ytext, { trackedOrigins: new Set([binding]) });
      // `editor.addCommand` overwrites whatever was previously bound to the
      // same keychord (Monaco's documented override mechanism) — no disposable
      // to track: the next file switch's effect run simply re-registers these
      // same three keychords pointing at that file's own undoManager before
      // this one is destroyed below, so there's never a window where a stale
      // undoManager could still be invoked.
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyZ, () => {
        undoManager.undo();
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyZ, () => {
        undoManager.redo();
      });
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyY, () => {
        undoManager.redo();
      });

      liveRef.current = true;
      setStatus('live');
      setLostPath(null);
      publishCursor();
    };
    const onSync = (isSynced) => {
      if (isSynced) {
        bind();
      }
    };
    provider.on('sync', onSync);

    const renderRemoteCursors = () => {
      const manager = managerRef.current;
      if (!manager) {
        return;
      }
      const seen = new Set();
      awareness.getStates().forEach((state, clientId) => {
        if (clientId === awareness.clientID || !state?.user || !state?.cursor) {
          return;
        }
        seen.add(state.user.id);
        manager.updateCursor(state.user.id, state.user.name, inkFor(state.user.slot ?? 0), state.cursor, { tint: tintFor(state.user.slot ?? 0), initials: state.user.initials });
      });
      manager.remoteCursors.forEach((_data, remoteUserId) => {
        if (!seen.has(remoteUserId)) {
          manager.removeCursor(remoteUserId);
        }
      });
    };
    awareness.on('change', renderRemoteCursors);

    // Lets CollaborationPanel (a sibling component with no direct access to
    // this file-scoped awareness instance) show "who's here" for the project.
    registerAwareness(projectId, awareness);

    return () => {
      unregisterAwareness(projectId, awareness);
      awareness.off('change', renderRemoteCursors);
      provider.off('sync', onSync);
      provider.off('status', onStatus);
      window.removeEventListener('online', onOnline);
      clearTimeout(retryTimer);
      ytext.unobserve(onLocalEdit);
      cursorDisposable.dispose();
      selectionDisposable.dispose();
      clearTimeout(cursorThrottle);
      clearTimeout(typingTimer);
      clearTimeout(syncTimer);
      undoManager?.destroy();
      binding?.destroy();
      liveRef.current = false;
      setStatus('off');
      setMyCursor(null);
      awarenessRef.current = null;
      awareness.setLocalState(null);
      setPresenceCursor(projectId, null);
      setPresenceView(projectId, 'editor', null);
      provider.destroy();
      ydoc.destroy();
    };
  }, [editor, monaco, file?.path || file?.name, projectId, serviceName, attempt]);

  const filePath = file?.path || file?.name;

  return {
    collaborationManager: managerRef.current,
    getCollaborators: () => managerRef.current?.getActiveCursors() ?? [],
    // True once this file is bound to the live doc: the editor's own saves stay local then.
    isLive: () => liveRef.current,
    // While a lost connection is made again, the file still reads as not reachable (not 'connecting').
    status: status === 'connecting' && lostPath && lostPath === `${serviceName}/${filePath}` ? 'disconnected' : status,
    presence,
    myCursor,
  };
};

export default useCollaboration;
